import { useMemo, useContext } from 'react';

import { LocationsGeoFilterInput } from '@ocx/graphql';
import { useConfiguration } from '@ocx/data-configuration';

import { LocalOffersLocationContext } from '../context';

type UseOffersLocationsGeoFilterOptions = {
  /**
   * When true, includes currentCoordinates in the filter even when locationState is 'all'.
   * When false, returns undefined when locationState is 'all'.
   * @default true
   */
  includeCoordinatesWhenShowingAll?: boolean;
};

export const useOffersLocationsGeoFilter = (options: UseOffersLocationsGeoFilterOptions = {}) => {
  const { includeCoordinatesWhenShowingAll = true } = options;
  const configuration = useConfiguration();

  const localOffersConfig = configuration.config.localOffers;
  const { enabled: isLocalOffersFeatureEnabled } = localOffersConfig;

  // Use useContext directly instead of the hook that throws errors
  // This allows the hook to work even when the context provider is not present
  const contextData = useContext(LocalOffersLocationContext);
  const locationState = contextData?.locationState ?? null;
  const currentCoordinates = contextData?.currentCoordinates ?? null;
  const customLocation = contextData?.customLocation ?? null;

  const locationsGeoFilter: LocationsGeoFilterInput | undefined = useMemo(() => {
    if (!isLocalOffersFeatureEnabled) {
      return undefined;
    }

    const { radiusInMeters } = localOffersConfig;

    if (locationState === 'nearby' && currentCoordinates) {
      return {
        latitude: currentCoordinates.lat,
        longitude: currentCoordinates.lng,
        radiusInMeters,
        restrictGeoFiltering: false,
      };
    }

    if (locationState === 'all' && currentCoordinates && includeCoordinatesWhenShowingAll) {
      return {
        latitude: currentCoordinates.lat,
        longitude: currentCoordinates.lng,
        radiusInMeters,
        restrictGeoFiltering: true,
      };
    }

    if (locationState === 'custom' && customLocation) {
      return {
        latitude: customLocation.coordinates.lat,
        longitude: customLocation.coordinates.lng,
        radiusInMeters,
        restrictGeoFiltering: false,
      };
    }

    return undefined;
  }, [
    isLocalOffersFeatureEnabled,
    currentCoordinates,
    localOffersConfig,
    locationState,
    customLocation,
    includeCoordinatesWhenShowingAll,
  ]);

  return locationsGeoFilter;
};
